<template>
    <div class="product-details-modal-overlay">
        <transition name="modal-expand">
            <div v-if="isVisible" class="product-details-modal" @click.stop>
                <div class="modal-content">
                    <font-awesome-icon class="window-close-icon" :icon="['fas', 'window-close']" @click="close" />
                    <h2>Details of {{ $labels.product }} ID: {{ selectedProductId }}</h2>

                    <div class="form-group">
                        <label>{{ $labels.product }} Name:</label>
                        <span class="form-value">{{ newProduct.name || '—' }}</span>
                    </div>
                    <div class="form-group">
                        <label>Code:</label>
                        <span class="form-value">{{ newProduct.product_code || '—' }}</span>
                    </div>
                    <div class="form-group">
                        <label>Pass Rate:</label>
                        <span class="form-value">{{ newProduct.pass_rate || '—' }}%</span>
                    </div>
                    <div class="form-group">
                        <label>Tasks:</label>
                        <span class="form-value">{{ taskCount ?? '—' }}</span>
                    </div>
                    <!-- Tabs for projects -->
                    <div class="project-tabs">
                        <button v-for="(project, index) in projects" :key="project.project_id"
                            @click="changeProject(index)" :class="{ active: selectedProject === index }"
                            class="project-tab">
                            {{ $labels.project }} {{ index + 1 }}
                        </button>
                    </div>

                    <div class="add">
                        <form @submit.prevent="updateProduct">
                            <div v-if="projects.length">
                                <div class="form-group">
                                    <label>Start Date:</label>
                                    <span class="form-value">{{ displayDate(projects[selectedProject].start_date) }}</span>
                                </div>
                                <div class="form-group">
                                    <label>End Date:</label>
                                    <span class="form-value">{{ displayDate(projects[selectedProject].end_date) }}</span>
                                </div>
                                <div class="form-group" style="padding-bottom: 15px;">
                                    <label>Location:</label>
                                    <span class="form-value">{{ projects[selectedProject].location || '—' }}</span>
                                </div>
                            </div>

                            <fieldset class="logbook-details-box">
                                <legend class="cell-header">Instructors</legend>
                                <div class="trainee-container">
                                    <div v-if="instructorNames.length === 0" class="trainee-box-red">
                                        No instructors assigned
                                    </div>
                                    <div v-else v-for="(name, index) in instructorNames" :key="index"
                                        class="trainee-box">
                                        {{ name }}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset class="logbook-details-box">
                                <legend class="cell-header">Trainees</legend>
                                <div class="trainee-container">
                                    <div v-if="traineeNames.length === 0" class="trainee-box-red">
                                        No trainees assigned
                                    </div>
                                    <div v-else v-for="(name, index) in traineeNames" :key="index" class="trainee-box">
                                        {{ name }}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset class="logbook-details-box">
                                <legend class="cell-header">Logbooks</legend>
                                <div class="trainee-container">
                                    <div v-if="logbooks.length === 0" class="trainee-box-red">
                                        No logbooks assigned
                                    </div>
                                    <div v-else v-for="logbook in logbooks" :key="logbook.logbook_id"
                                        class="trainee-box">
                                        {{ logbook.name }}
                                    </div>
                                </div>
                            </fieldset>


                            <div v-if="showAlert" class="alert alert-danger error-message">
                                <div v-if="errorMessage">{{ errorMessage }}</div>
                            </div>

                            <div class="button-container">
                                <button class="ins-close-button" @click="close()">Close</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ProductDetailsModal',
    data() {
        return {
            instructors: [],
            trainees: [],
            logbooks: [],
            taskCount: null,
            newProduct: {
                name: '',
                product_code: '',
            },
            projects: [], // List of all projects
            selectedProject: 0, // Track selected tab
            showAlert: false,
            errorMessage: '',
            selectedProductId: localStorage.getItem('product_id'),
            isVisible: false,
        };
    },
    computed: {
        traineeNames() {
            return this.trainees.map(trainee =>
                trainee.first_name && trainee.last_name
                    ? `${trainee.first_name} ${trainee.last_name}`
                    : 'Unknown'
            );
        },
        instructorNames() {
            return this.instructors.map(instructor =>
                instructor.first_name && instructor.last_name
                    ? `${instructor.first_name} ${instructor.last_name}`
                    : 'Unknown'
            );
        },
    },
    methods: {
        close() {
            this.$emit('close'); // Emit an event to indicate the modal should be closed
        },
        changeProject(index) {
            this.selectedProject = index;
            this.fetchUsers(); // Fetch users for the selected project
            this.fetchLogbooks(); // Fetch logbooks for the selected project
        },
        async getProductData(product_id) {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            try {
                const res = await axios.get(`/api/products/${product_id}`, { headers });
                console.log('product', res.data);
                this.newProduct = res.data;

            } catch (error) {
                console.error('An error occurred while fetching data:', error);
            }
        },
        async getTaskCount(product_id) {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            try {
                // Same scoped endpoint productPage.vue uses; unfiltered (no
                // project_id === null check) so the count matches what
                // products.vue's own "Task count" column already shows.
                const res = await axios.get(`/api/tasks/products/${product_id}`, { headers });
                this.taskCount = res.data.length;
            } catch (error) {
                console.error('Error fetching task count:', error);
                this.taskCount = null;
            }
        },
        async getProjectData(product_id) {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            try {
                const res = await axios.get(`/api/products/${product_id}/projects`, { headers });
                this.projects = res.data.map(project => ({
                    ...project,
                    start_date: this.formatDate(project.start_date),
                    end_date: this.formatDate(project.end_date),
                }));

                // If projects exist, fetch users for the first project automatically
                if (this.projects.length > 0) {
                    this.selectedProject = 0; // Ensure first project is selected
                    this.fetchUsers();
                    this.fetchLogbooks();
                }
            } catch (error) {
                console.error('Error fetching projects:', error);
            }
        },
        async fetchUsers() {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    console.error('Unauthorized access');
                    return;
                }

                const headers = { Authorization: `Bearer ${token}` };

                if (!this.projects.length || this.selectedProject === null) {
                    console.warn("No projects available or project not selected");
                    return;
                }

                const projectId = this.projects[this.selectedProject].project_id;

                const response = await axios.get(`/api/projects/${projectId}/users`, { headers });

                if (response.status === 200) {
                    this.instructors = response.data.filter(user => user.pivot.role === 'instructor');
                    this.trainees = response.data.filter(user => user.pivot.role === 'trainee');
                }
            } catch (error) {
                console.error('Error retrieving users:', error);
            }
        },
        async fetchLogbooks() {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    console.error('Unauthorized access');
                    return;
                }

                const headers = { Authorization: `Bearer ${token}` };

                if (!this.projects.length || this.selectedProject === null) {
                    console.warn("No projects available or project not selected");
                    return;
                }

                const projectId = this.projects[this.selectedProject].project_id;

                const response = await axios.get(`/api/projects/${projectId}/logbooks`, { headers });

                if (response.status === 200) {
                    this.logbooks = response.data;
                    console.log('logbooks', this.logbooks);
                }
            } catch (error) {
                console.error('Error retrieving logbooks:', error);
                this.logbooks = [];
            }
        },
        formatDate(date) {
            const [day, month, year] = date.split('/');
            return `${year}-${month}-${day}`;
        },
        // formatDate() above stores dates as "YYYY-MM-DD" (for the old date
        // input's v-model); this renders that back out as "DD/MM/YYYY" for
        // plain-text display.
        displayDate(isoDate) {
            if (!isoDate) return '—';
            const [year, month, day] = isoDate.split('-');
            return year && month && day ? `${day}/${month}/${year}` : isoDate;
        },
    },
    async mounted() {
        this.isVisible = true;
        this.getProductData(parseInt(localStorage.getItem('product_id')));
        this.getProjectData(parseInt(localStorage.getItem('product_id')));
        this.getTaskCount(parseInt(localStorage.getItem('product_id')));
    },
}
</script>

<style scoped>
.project-tabs {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    margin-left: 20px;
    margin-right: 20px;
    border-bottom: 1px solid #969696
}

.project-tab {
    padding: 8px 12px;
    background: #ddd;
    border: none;
    cursor: pointer;
    border-radius: 5px 5px 0 0;
    border: 1px solid #ddd
}

.project-tab.active {
    background: #007bff;
    color: white;
    border: 1px solid blue;
}

.modal-content {
    position: relative;
    max-height: 90vh;
    overflow-y: auto;
    padding: 15px;
}

.modal-content h2 {
    margin-top: 10px;
    font-size: 30px;
    margin-bottom: 30px;
}

.product-details-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    /* Semi-transparent background */
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    /* Ensure the modal is on top of other content */
}

.product-details-modal {
    background-color: #fff;
    border-radius: 5px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    max-width: 70vw;
    max-height: 85vh;
    min-width: max(60vw, 1000px);
    /* Limit the maximum width of the modal */
    padding: 20px;
    position: relative;
    max-height: 95%;
}

@media (max-width: 768px) {

    /* Adjust the max-width as needed for iPad or other tablet sizes */
    .product-details-modal {
        padding: 5px;
        font-size: 0.9em;
        /* Reduce font size */
    }

    /* Further adjustments for other elements */
}



.alert {
    position: relative;
    padding: .75rem 1.25rem;
    margin-bottom: 0rem;
    border: 1px solid transparent;
    border-radius: .25rem;
}


button.btn:hover {
    background-color: #0c1a61;
    transition: transform 0.2s, box-shadow 0.2s;
}

.form-group {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
}

.form-group label {
    width: 200px;
    margin-left: 30px;
    margin-right: 10px;
    text-align: left;
    margin-bottom: 0px;
    font-size: 16px;
    font-weight: 600;
    color: #444;
}

.form-value {
    flex: 1;
    text-align: left;
    color: #1a1a1a;
    font-size: 16px;
}

h1 {
    margin-bottom: 50px;
}

.button-container {
    display: flex;
    justify-content: space-between;
    /* Aligns items to the right */
    align-items: center;
    margin-top: 0px;
    /* Adjust as needed */
}

.ins-close-button {
    background-color: #6c757d;
    border: 1px solid #4d5358;
    color: white;
    width: 120px;
    padding: 5px 20px;
    border-radius: 5px;
    margin-left: 5%;
}

.ins-close-button:hover {
    background-color: #4d5358;
}

.logbook-details-box {
    border: 2px solid #858585;
    border-radius: 5px;
    margin: 3px auto;
    min-width: 95%;
    max-width: 95%;
    padding: 7px;
    margin-bottom: 20px;
}


.cell-header {
    font-weight: bold;
    font-family: Verdana, Geneva, Tahoma, sans-serif;
    font-size: 12px;
    background-color: #707070;
    padding: 2px 5px;
    color: white;
    border-radius: 2px;
    margin-bottom: 5px;
    margin-top: -20px;
    /* Set margin-right */
    text-align: left;
    display: inline-block;
    width: auto;
}

.logbook-details {
    display: flex;
    flex-direction: column;
    text-align: left;
}

.trainee-container {
    display: flex;
    flex-wrap: wrap;
    /* Allows wrapping */
}

.trainee-box {
    display: inline-block;
    padding: 4px 12px;
    margin: 3px;
    border-radius: 8px;
    background-color: #f0f0f0;
    border: 1px solid #ccc;
    text-align: center;
    white-space: nowrap;
    font-size: 14px;
}

.trainee-box-red {
    display: inline-block;
    padding: 4px 12px;
    margin: 3px;
    border-radius: 8px;
    background-color: #ffd3d3;
    border: 1px solid #f84c4c;
    color: rgb(195 0 0);
    text-align: center;
    white-space: nowrap;
    font-size: 14px;
}

.ins-assign-button {
    margin-top: 0px;
    margin-bottom: 0px;
    margin-left: 15px;
    margin-right: 15px;
    border-radius: 5px;
    background-color: rgb(53, 131, 37);
    border: 1px solid rgb(26, 82, 16);
}

.ins-assign-button:hover {
    background-color: rgb(48, 112, 35);
    border: 1px solid rgb(23, 77, 13);
}
</style>